import React, { useEffect, useState } from "react";
import { ResponsiveLine } from "@nivo/line";

const HeartRateChart = ({ patientData, hasHr }) => {
  const [chartData, setChartData] = useState([]);
  const [yAxisRange, setYAxisRange] = useState({ min: 0, max: 0 });
  const [xTickValues, setxTickValues] = useState([]);

  // Define a brighter pink color scheme
  const pinkColor = "#ec4899"; // Brighter pink (Tailwind pink-500)
  const fillColor = "rgba(236, 72, 153, 0.2)"; // Solid fill color with 20% opacity

  useEffect(() => {
    if (
      patientData &&
      patientData.month_steps &&
      patientData.month_steps.length >= 5 &&
      hasHr
    ) {
      const dates = patientData.month_steps[4] || [];
      const hrData = patientData.month_steps[3] || [];

      // Create array of data points
      const processedData = dates
        .map((dateStr, index) => {
          const date = new Date(dateStr);
          return {
            date: dateStr,
            formattedDate: date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "2-digit",
            }),
            fullDate: date.toLocaleDateString("en-US", {
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            heartRate: hrData[index] || null,
          };
        })
        .filter(
          (item) =>
            !isNaN(new Date(item.date).getTime()) &&
            item.heartRate !== null &&
            item.heartRate !== 0
        ); // Filter out invalid dates and null/zero heart rates

      // Sort data by date
      processedData.sort((a, b) => new Date(a.date) - new Date(b.date));


      //Adjust tick interval based on number of points
      const everyNth = (arr, n) => arr.filter((_, i) => i % n === 0)

      var interval = 1;
      if(processedData.length > 100){
        interval = 10;
      }
      else if (processedData.length > 50) {
        interval = 5;
      }
      else {
        interval = 1;
      }

      const xTickValues = everyNth(processedData.map(item => item.formattedDate), interval)
      setxTickValues(xTickValues);

      // Format data for Nivo line chart
      const nivoData = [
        {
          id: "Heart Rate",
          data: processedData.map((item) => ({
            x: item.formattedDate,
            y: item.heartRate,
            date: item.date,
            fullDate: item.fullDate,
          })),
        },
      ];

      // Calculate Y-axis range to ensure proper spacing
      if (processedData.length > 0) {
        const rates = processedData.map((item) => item.heartRate);
        const minRate = Math.min(...rates);
        const maxRate = Math.max(...rates);
        const range = maxRate - minRate;

        // Add padding (10% of range) to top and bottom
        const padding = range * 0.1;

        setYAxisRange({
          min: Math.max(0, minRate - padding), // Ensure we don't go below 0
          max: maxRate + padding,
        });
      }

      setChartData(nivoData);
    }
  }, [patientData, hasHr]);

  if (!hasHr || chartData.length === 0) {
    return null;
  }

  // Simple, clean theme for Nivo
  const theme = {
    background: "transparent",
    textColor: "#333333",
    fontSize: 12,
    axis: {
      domain: {
        line: {
          stroke: "#dddddd",
          strokeWidth: 1,
        },
      },
      ticks: {
        line: {
          stroke: "#dddddd",
          strokeWidth: 1,
        },
        text: {
          fill: "#666666",
          fontSize: 11,
        },
      },
      legend: {
        text: {
          fill: "#666666",
          fontSize: 13,
        },
      },
    },
    grid: {
      line: {
        stroke: "#eeeeee",
        strokeWidth: 1,
      },
    },
    tooltip: {
      container: {
        background: "#ffffff",
        fontSize: 12,
        borderRadius: 3,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
        padding: "8px 10px",
      },
    },
  };

  // Custom tooltip component with pink styling
  const CustomTooltip = ({ point }) => (
    <div
      style={{
        padding: "10px 12px",
        background: "white",
        border: "1px solid #ccc",
        borderRadius: "3px",
        minWidth: "120px",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
        {point.data.fullDate || point.data.x}
      </div>
      <div style={{ color: pinkColor }}>
        Heart Rate: <strong>{point.data.y.toFixed(0)} bpm</strong>
      </div>
    </div>
  );

  return (
    <div className="graph">
      <h3>Heart Rate Overview</h3>

      <div style={{ height: "350px" }}>
        <ResponsiveLine
          data={chartData}
          margin={{ top: 40, right: 50, bottom: 50, left: 60 }}
          xScale={{ type: "point" }}
          yScale={{
            type: "linear",
            min: yAxisRange.min,
            max: yAxisRange.max,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legendOffset: 36,
            tickValues: xTickValues,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Heart Rate (bpm)",
            legendOffset: -50,
            legendPosition: "middle",
            format: (value) => value.toFixed(0),
          }}
          colors={[pinkColor]} // Brighter pink color
          pointSize={8}
          pointColor="white"
          pointBorderWidth={2}
          pointBorderColor={pinkColor} // Pink border for points
          enablePointLabel={false}
          pointLabel="y"
          enableGridX={false}
          enableGridY={true}
          enableArea={true}
          areaBaselineValue={yAxisRange.min} // Set baseline to the min value of y-axis
          areaOpacity={0.2} // Solid opacity with no gradient
          useMesh={true}
          theme={theme}
          tooltip={CustomTooltip}
          lineWidth={3} // Slightly thicker line
          // No gradient defs - using solid color instead
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          enableSlices={false}
          legends={[]}
          areaBlendMode="normal" // Normal blend mode for the area
          // Setting a solid fill color via custom layer function
          layers={[
            "grid",
            "markers",
            "areas",
            "crosshair",
            "lines",
            "axes",
            "points",
            "slices",
            "mesh",
            "legends",
          ]}
          // More direct color for the area
          areaColor={fillColor}
        />
      </div>
    </div>
  );
};

export default HeartRateChart;
